import { useEffect, useRef, useState } from 'react';
import '@tylertech/forge/dialog';
import '@tylertech/forge/button';
import '@tylertech/forge/icon';
import './KeyRevealModal.css';

export interface KeyRevealModalProps {
  open: boolean;
  rawKey: string | null;
  onDismiss: () => void;
}

const DialogElement = 'forge-dialog' as React.ElementType;
const ForgeButton = 'forge-button' as React.ElementType;

export function KeyRevealModal({
  open,
  rawKey,
  onDismiss,
}: KeyRevealModalProps): JSX.Element | null {
  const dialogRef = useRef<HTMLElement & { open: boolean }>(null);
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    if (dialogRef.current) {
      dialogRef.current.open = open;
    }
    if (!open) {
      setCopied(false);
      setCopyFailed(false);
      setConfirmed(false);
    }
  }, [open]);

  if (!open || rawKey === null) {
    return null;
  }

  const handleCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(rawKey);
      setCopyFailed(false);
      setCopied(true);
    } catch {
      setCopied(false);
      setCopyFailed(true);
    }
  };

  const titleId = 'key-reveal-title';
  const descId = 'key-reveal-desc';

  return (
    <DialogElement
      ref={dialogRef}
      className="key-reveal-modal"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={descId}
    >
      <div className="dialog-content dialog-content--flow">
        <div className="key-reveal-header">
          <forge-icon name="vpn_key" className="key-reveal-icon" aria-hidden="true"></forge-icon>
          <h2 id={titleId} className="forge-typography--headline5">
            Your New API Key
          </h2>
        </div>

        <div id={descId} className="key-reveal-body">
          <p className="forge-typography--body1 key-reveal-warning">
            This key will never be shown again. Copy it now.
          </p>
          <div className="key-reveal-key-block">
            <code className="key-reveal-key-value" data-testid="raw-key-value">
              {rawKey}
            </code>
            <ForgeButton
              variant="outlined"
              className="key-reveal-copy-btn"
              onClick={handleCopy}
              type="button"
              aria-label="Copy API key to clipboard"
            >
              <forge-icon
                name={copyFailed ? 'error' : copied ? 'check' : 'content_copy'}
                aria-hidden="true"
              ></forge-icon>
              {copyFailed ? 'Failed to copy' : copied ? 'Copied!' : 'Copy'}
            </ForgeButton>
          </div>

          {copyFailed && (
            <p className="key-reveal-copy-error" role="alert">
              Couldn&apos;t copy automatically. Select the key text above and copy it manually.
            </p>
          )}

          <label className="key-reveal-confirm-label">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              data-testid="confirm-checkbox"
            />
            <span>I&apos;ve copied my key</span>
          </label>
        </div>

        <div className="dialog-actions dialog-actions--borderless">
          <ForgeButton
            variant="raised"
            onClick={onDismiss}
            disabled={!confirmed}
            type="button"
            data-testid="dismiss-button"
          >
            Done
          </ForgeButton>
        </div>
      </div>
    </DialogElement>
  );
}
